<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Por Status',
  },
});

// Calculate total
const total = computed(() => {
  return props.data.reduce((sum, item) => sum + (item.value || 0), 0);
});

// Generate SVG arc path
function describeArc(cx, cy, radius, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(' ');
}

function polarToCartesian(cx, cy, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
}

// Generate arcs for donut chart
const arcs = computed(() => {
  if (total.value === 0) return [];

  const cx = 100;
  const cy = 100;
  const radius = 80;
  let currentAngle = 0;

  return props.data.map(item => {
    const percentage = (item.value / total.value) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    return {
      ...item,
      percentage: percentage.toFixed(1),
      path: describeArc(
        cx,
        cy,
        radius,
        startAngle,
        Math.min(endAngle, startAngle + 359.9)
      ),
    };
  });
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <!-- Header -->
    <div class="mb-4">
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="total === 0"
      class="flex items-center justify-center h-64 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponíveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex items-center gap-6">
      <!-- Donut Chart -->
      <div class="flex-shrink-0 w-48 h-48">
        <svg viewBox="0 0 200 200" class="w-full h-full">
          <!-- Background circle -->
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="currentColor"
            stroke-opacity="0.1"
            stroke-width="24"
          />

          <!-- Data arcs -->
          <path
            v-for="(arc, index) in arcs"
            :key="index"
            :d="arc.path"
            fill="none"
            :stroke="arc.color"
            stroke-width="24"
            stroke-linecap="round"
          />

          <!-- Center text -->
          <text
            x="100"
            y="95"
            text-anchor="middle"
            class="fill-n-slate-12 text-2xl font-bold"
          >
            {{ total }}
          </text>
          <text
            x="100"
            y="115"
            text-anchor="middle"
            class="fill-n-slate-11 text-xs"
          >
            total
          </text>
        </svg>
      </div>

      <!-- Legend -->
      <div class="flex-1 space-y-3">
        <div
          v-for="(item, index) in data"
          :key="index"
          class="flex items-center justify-between"
        >
          <div class="flex items-center gap-2">
            <span
              class="w-3 h-3 rounded-full"
              :style="{ backgroundColor: item.color }"
            />
            <span class="text-sm text-n-slate-11">{{ item.label }}</span>
          </div>
          <span class="text-sm font-semibold text-n-slate-12">
            {{ item.value.toLocaleString('pt-BR') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
